// 젬 상점 탭(잡화상 창 「젬」 탭, docs/IAP-PRD.md §8) + 설정의 구매 내역 묶음.
// 상점 창 안에서만 그린다(플레이 화면 위에 새 팝업을 띄우지 않는다). 결제창 자체는 포트원 SDK가 띄운다.
import { C, FONT, FONT_NUM, el } from '../ui/dom';
import { BUSINESS_INFO, LEGAL_LINKS } from '../community/config';
import { iapClient, isTester, type IapClient } from './client';
import { playPriceText, PLAY_TEST_BAND } from './play';
import { APP_PAY_NOTICE, isAppChannel } from '../shared/appChannel';
import { MINOR_CHECK_LABEL, MINOR_NOTICE_TEXT, MINOR_UNCHECKED_ERROR, buyerError, gems, monthLimitText, orderStatusText, productExtras, productLines, shopVisible, won, withdrawShort, WITHDRAW_NOT_AFTER_USE, type IapProductView, type ShopMode } from './model';

const CARD = 'display:flex;flex-direction:column;gap:6px;padding:12px;border-radius:12px;border:1.5px solid rgba(242,193,78,.4);background:linear-gradient(180deg,rgba(36,48,90,.92),rgba(16,22,42,.92));min-width:0;';
const BTN = 'pointer-events:auto;cursor:pointer;min-height:var(--dh-btn-min,44px);padding:8px 14px;border-radius:10px;font:900 15px ' + FONT + ';white-space:nowrap;';
const GOLD = BTN + 'border:2px solid #fff1b8;background:linear-gradient(180deg,#ffe597,#e1a536);color:#2a1d05;';
const PLAIN = BTN + 'border:2px solid ' + C.gold + ';background:linear-gradient(#26335a,#1A2238);color:' + C.gold + ';';

function btn(parent: HTMLElement, label: string, css: string, testid: string, onClick: () => void, disabled = false): HTMLButtonElement {
  const b = el('button', css + (disabled ? 'opacity:.45;cursor:default;' : ''), parent, label);
  b.type = 'button';
  b.dataset.testid = testid;
  b.disabled = disabled;
  b.addEventListener('click', onClick);
  return b;
}

/** 테스트 모드 띠: 실결제가 아니면 상점 맨 위에 늘 보인다 */
function testBand(parent: HTMLElement, live: boolean, text = '테스트 결제 — 실제 청구 없음 · 가격·구성은 가안'): void {
  if (live) return;
  const b = el('div', 'padding:7px 12px;border-radius:9px;background:repeating-linear-gradient(135deg,#5a3d00 0 12px,#4a3200 12px 24px);border:1.5px solid #ffcf5a;color:#ffe9a8;font:900 14px ' + FONT + ';text-align:center;', parent,
    text);
  b.dataset.testid = 'iap-test-band';
}

/**
 * 상점 하단의 작은 법적 링크 줄 + 사업자 정보(전자상거래법 표시, KG이니시스 심사). 상점 창 안 가장자리에만 그리고 플레이 화면 위에는 아무것도 띄우지 않는다.
 * 링크는 새 탭으로 열어 결제 진행을 끊지 않는다. 값은 src/community/config.ts 정본(공개 페이지 푸터와 같음).
 */
function mountLegalLine(parent: HTMLElement, testid: string): void {
  const wrap = el('div', 'display:flex;flex-direction:column;gap:4px;padding-top:6px;border-top:1px solid rgba(242,193,78,.2);', parent);
  wrap.dataset.testid = testid;
  const links = el('div', 'display:flex;flex-wrap:wrap;gap:0 14px;', wrap);
  for (const l of LEGAL_LINKS) {
    const a = el('a', 'pointer-events:auto;font:600 12px ' + FONT + ';color:#ffd877;text-decoration:underline;padding:6px 0;', links, l.label);
    a.href = l.href; a.target = '_blank'; a.rel = 'noopener noreferrer';
  }
  const b = BUSINESS_INFO;
  el('div', 'font:500 11px/1.55 ' + FONT + ';color:#7f8db3;', wrap,
    `상호 ${b.name} · 대표자 ${b.representative} · 사업자등록번호 ${b.registrationNo} · 통신판매업 신고번호 ${b.mailOrderNo} · 주소 ${b.address} · 연락처 ${b.email} · ${b.phone} · 게임제작업 등록번호 ${b.gameMakerNo}`);
}

export class IapShopPanel {
  readonly client: IapClient = iapClient();
  private readonly tester = isTester();
  private selected: string | null = null;
  private buyer = { name: '', phone: '', email: '' };
  private formErr: string | null = null;
  private minorChecked = false;
  private refreshedAt = 0;

  /** 잡화상 탭 줄에 「젬」 탭을 보일지 */
  get available(): boolean { return this.client.mode() !== null; }
  private get mode(): ShopMode | null { return this.client.mode(); }
  get version(): number { return this.client.version; }

  render(body: HTMLElement): void {
    const c = this.client, cfg = c.config;
    if (!cfg) { el('div', 'padding:20px;color:#9fb0d8;font:600 15px ' + FONT, body, '지금은 젬 상점을 열 수 없어요.'); return; }
    // 원스토어 앱: 상품 목록·구매 시트 대신 짧은 안내만(웹 PG 결제 미노출). 젬 사용은 영향 없음
    const mode = this.mode;
    if (mode === 'app-notice' || (isAppChannel() && mode === null)) { el('div', 'padding:14px 16px;border-radius:12px;background:rgba(8,12,28,.6);border:1px solid rgba(242,193,78,.3);color:#cfe0ff;font:700 15px ' + FONT, body, APP_PAY_NOTICE).dataset.testid = 'iap-app-notice'; return; }
    // 탭을 열 때(최대 30초마다) 지갑·내역을 새로 받는다
    if (performance.now() - this.refreshedAt > 30_000) { this.refreshedAt = performance.now(); void c.refresh(); }
    // 오른쪽 여백: 화면 오른쪽 가장자리의 커뮤니티 버튼(가로 화면에서 창 위에 떠 있다)이 카드 버튼을 가리지 않게
    const root = el('div', 'display:flex;flex-direction:column;gap:10px;padding-right:clamp(0px,calc(100vw - 600px),44px);', body);
    root.dataset.testid = 'iap-shop';
    const play = mode === 'play';
    if (play) testBand(root, cfg.play?.stage === 'live', PLAY_TEST_BAND); else testBand(root, cfg.live);
    c.viewOnce();

    // 지갑 줄
    const w = c.wallet;
    const top = el('div', 'display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding:10px 12px;border-radius:12px;background:rgba(8,12,28,.6);border:1px solid rgba(242,193,78,.3);', root);
    const gemIcon = '<svg viewBox="0 0 24 24" width="26" height="26"><path d="M6 3h12l4 6-10 12L2 9z" fill="#7fe3ff" stroke="#e8fbff" stroke-width="1.2"/><path d="M2 9h20M8 3l4 18M16 3l-4 18" stroke="#2a8fb0" stroke-width="1"/></svg>';
    const g = el('div', 'display:flex;align-items:center;gap:8px;', top);
    g.innerHTML = gemIcon;
    const total = el('b', 'font:900 26px ' + FONT_NUM + ';color:#e8fbff;letter-spacing:.5px;', g, w ? gems(w.total) : '—');
    total.dataset.testid = 'iap-wallet-total';
    el('span', 'font:700 13px ' + FONT + ';color:#9fb0d8;', top, w ? `유료 ${gems(w.paid)} · 무료 ${gems(w.free)}` : '잔액 확인 중…');
    const ml = monthLimitText(w);
    if (ml) el('span', 'font:600 12px ' + FONT + ';color:#9fb0d8;', top, ml).dataset.testid = 'iap-month-limit';
    if (w && w.debt > 0) el('span', 'font:800 13px ' + FONT + ';color:#ff9a9a;', top, `환불 회수 미정산 ${gems(w.debt)} — 다음 충전에서 먼저 차감, 그동안 사용 불가`);
    if (w && w.refundPending) el('span', 'font:800 13px ' + FONT + ';color:#ffd877;', top, '환불 요청 처리 중 — 처리 전까지 젬 사용 잠김');
    if (w?.monthly) {
      const m = el('div', 'margin-left:auto;display:flex;align-items:center;gap:8px;', top);
      el('span', 'font:700 13px ' + FONT + ';color:#cfe0ff;', m, `월정액 ~${new Date(w.monthly.endsAt).toLocaleDateString('ko-KR')}`);
      btn(m, w.monthly.claimedToday ? '오늘 받음' : `오늘 젬 ${gems(w.monthly.daily)} 받기`, GOLD, 'iap-claim', () => void c.claimDaily(), w.monthly.claimedToday);
    }

    // 진행·완료·실패 줄
    const ph = c.phase;
    if (ph.kind !== 'idle') {
      const tone = ph.kind === 'busy' ? '#cfe0ff' : ph.kind === 'done' ? '#9ff7b4' : '#ffb0b0';
      const row = el('div', 'display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:10px;background:rgba(0,0,0,.35);border:1px solid ' + tone + '55;', root);
      row.dataset.testid = 'iap-phase-' + ph.kind;
      el('span', 'flex:0 1 auto;font:800 15px ' + FONT + ';color:' + tone + ';line-height:1.4;', row, (ph.kind === 'busy' ? '⏳ ' : ph.kind === 'done' ? '✔ ' : '✖ ') + ph.text);
      if (ph.kind !== 'busy') btn(row, '확인', PLAIN, 'iap-phase-ok', () => c.clearPhase());
    }

    // 구매 시트(선택한 상품) 또는 상품 목록
    const sel = this.selected ? cfg.products.find((p) => p.id === this.selected) ?? null : null;
    const products = play ? cfg.products.filter((p) => p.playSku) : cfg.products;
    const live = play ? cfg.play?.stage === 'live' : cfg.live;
    if (sel && ph.kind !== 'busy') this.renderSheet(root, sel, live, cfg.withdrawDays, play);
    else this.renderGrid(root, products, ph.kind === 'busy', play);

    const foot = el('div', 'font:600 12px ' + FONT + ';color:#8e9cc4;line-height:1.5;', root);
    foot.textContent = `젬은 게임 안 편의·외형에만 써요(확률형 상품 없음). 결제 후 ${cfg.withdrawDays}일 안·미사용이면 설정 → 구매 내역에서 청약철회할 수 있어요.`;
    const pl = el('a', 'pointer-events:auto;align-self:flex-start;font:700 12px ' + FONT + ';color:#ffd877;text-decoration:underline;padding:8px 0;min-height:44px;box-sizing:border-box;', root, '상품·가격 안내');
    pl.href = '/pricing'; pl.target = '_blank'; pl.rel = 'noopener noreferrer'; pl.dataset.testid = 'iap-pricing-link';
    mountLegalLine(root, 'iap-legal');
  }

  /** 카드·시트에 보일 가격: Play는 현지 가격 우선 */
  private priceText(p: IapProductView, play: boolean): string {
    return play ? playPriceText(p.price, p.playSku ? this.client.playPrices.get(p.playSku) : null) : won(p.price);
  }

  private renderGrid(root: HTMLElement, products: IapProductView[], busy: boolean, play: boolean): void {
    const grid = el('div', 'display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:10px;', root);
    for (const p of products) {
      const bought = this.client.bought.has(p.id);
      const l = productLines(p, bought), x = productExtras(p, bought);
      const card = el('div', CARD + (p.enabled ? '' : 'opacity:.6;'), grid);
      card.dataset.testid = 'iap-product-' + p.id;
      const head = el('div', 'display:flex;align-items:center;gap:6px;flex-wrap:wrap;', card);
      el('b', 'font:900 17px ' + FONT + ';color:#fff;', head, l.title);
      const badge = !p.enabled ? l.badge : x.firstBadge;
      if (badge) el('span', 'padding:1px 7px;border-radius:9px;background:' + (p.enabled ? 'linear-gradient(180deg,#ff5a5a,#c81e2e)' : '#3a4466') + ';color:#fff;font:900 11px ' + FONT + ';', head, badge).dataset.testid = 'iap-badge-' + p.id;
      el('div', 'font:700 14px ' + FONT + ';color:#cfe0ff;line-height:1.4;flex:1;', card, l.detail + (x.bonusRatio ? ` (${x.bonusRatio})` : ''));
      if (x.note) el('div', 'font:700 12px ' + FONT + ';color:#ffd9a0;', card, x.note).dataset.testid = 'iap-note-' + p.id;
      const wd = withdrawShort(p.kind, this.client.config?.withdrawDays ?? 7);
      if (wd) el('div', 'font:600 11px/1.4 ' + FONT + ';color:#9fb0d8;', card, wd).dataset.testid = 'iap-withdraw-' + p.id;
      const foot = el('div', 'display:flex;align-items:center;justify-content:space-between;gap:8px;', card);
      el('b', 'font:900 18px ' + FONT_NUM + ';color:#ffd877;', foot, this.priceText(p, play));
      btn(foot, x.owned ? '구매 완료' : l.cta, GOLD, 'iap-buy-' + p.id, () => { this.selected = p.id; this.formErr = null; this.minorChecked = false; this.client.clearPhase(); this.client.version++; }, !p.enabled || busy || x.owned);
    }
  }

  private renderSheet(root: HTMLElement, p: IapProductView, live: boolean, withdrawDays: number, play: boolean): void {
    const bought = this.client.bought.has(p.id);
    const l = productLines(p, bought), x = productExtras(p, bought);
    const priceStr = this.priceText(p, play);
    const sh = el('div', CARD + 'gap:8px;', root);
    sh.dataset.testid = 'iap-sheet';
    el('b', 'font:900 18px ' + FONT + ';color:#fff;', sh, `${l.title} · ${priceStr}`);
    el('div', 'font:700 14px ' + FONT + ';color:#cfe0ff;', sh, l.detail + (x.firstBadge ? ` · ${x.firstBadge}` : ''));
    if (x.note) el('div', 'font:700 12px ' + FONT + ';color:#ffd9a0;', sh, x.note);
    const wd = withdrawShort(p.kind, withdrawDays);
    if (wd) el('div', 'font:800 12px ' + FONT + ';color:#9ff7b4;line-height:1.45;', sh, `${wd} · ${WITHDRAW_NOT_AFTER_USE}`).dataset.testid = 'iap-sheet-withdraw';
    if (!play) el('div', 'font:600 12px ' + FONT + ';color:#9fb0d8;line-height:1.45;', sh,
      '카드 결제(KG이니시스)에 필요한 구매자 정보예요. 결제사로만 전달되고 게임 서버에는 저장하지 않아요.');
    const field = (label: string, key: 'name' | 'phone' | 'email', type: string, ph: string): void => {
      const row = el('label', 'display:flex;align-items:center;gap:8px;font:700 14px ' + FONT + ';color:#e8eefc;', sh);
      el('span', 'width:64px;flex:0 0 auto;', row, label);
      const inp = el('input', 'flex:1;min-width:0;padding:8px 10px;border-radius:8px;border:1.5px solid rgba(242,193,78,.45);background:#0c1226;color:#fff;font:600 15px ' + FONT + ';', row);
      inp.type = type; inp.placeholder = ph; inp.value = this.buyer[key]; inp.autocomplete = key === 'name' ? 'name' : key === 'phone' ? 'tel' : 'email';
      inp.dataset.testid = 'iap-buyer-' + key;
      inp.addEventListener('input', () => { this.buyer[key] = inp.value; });
    };
    if (!play) {
      field('이름', 'name', 'text', '홍길동');
      field('휴대폰', 'phone', 'tel', '010-1234-5678');
      field('이메일', 'email', 'email', 'me@example.com');
    }
    el('div', 'font:600 12px ' + FONT + ';color:#ffd9a0;line-height:1.45;', sh,
      `결제 후 ${withdrawDays}일 안·젬을 쓰지 않았으면 청약철회(전액 환불)할 수 있어요. 젬을 썼거나 월정액 일일 젬을 받은 뒤에는 이 화면의 청약철회 요청을 쓸 수 없고, 쓰지 않은 부분은 고객센터에서 처리해요. ` +
      (live ? '' : '지금은 테스트 결제라 실제로 청구되지 않아요.'));
    el('div', 'font:700 12px ' + FONT + ';color:#ffd9a0;line-height:1.45;', sh, MINOR_NOTICE_TEXT);
    const chk = el('label', 'display:flex;align-items:center;gap:10px;min-height:44px;padding:6px 8px;border-radius:9px;border:1.5px solid rgba(242,193,78,.45);background:rgba(8,12,28,.5);cursor:pointer;pointer-events:auto;font:700 13px ' + FONT + ';color:#e8eefc;line-height:1.4;', sh);
    const cb = el('input', 'width:24px;height:24px;flex:0 0 auto;accent-color:#e1a536;margin:0;cursor:pointer;', chk);
    cb.type = 'checkbox'; cb.checked = this.minorChecked; cb.dataset.testid = 'iap-minor-check';
    el('span', 'flex:1;min-width:0;', chk, MINOR_CHECK_LABEL);
    cb.addEventListener('change', () => { this.minorChecked = cb.checked; if (cb.checked && this.formErr === MINOR_UNCHECKED_ERROR) { this.formErr = null; this.client.version++; } });
    const sl = el('a', 'pointer-events:auto;align-self:flex-start;font:700 12px ' + FONT + ';color:#ffd877;text-decoration:underline;padding:8px 0;min-height:44px;box-sizing:border-box;', sh, '상품·가격 안내');
    sl.href = '/pricing'; sl.target = '_blank'; sl.rel = 'noopener noreferrer';
    if (this.formErr) el('div', 'font:800 13px ' + FONT + ';color:#ffb0b0;', sh, this.formErr).dataset.testid = 'iap-form-error';
    const row = el('div', 'display:flex;gap:8px;flex-wrap:wrap;', sh);
    btn(row, live ? `${priceStr} 결제하기` : `테스트 결제 ${priceStr}`, GOLD, 'iap-pay', () => {
      const err = (play ? null : buyerError(this.buyer)) ?? (this.minorChecked ? null : MINOR_UNCHECKED_ERROR);
      if (err) { this.formErr = err; this.client.version++; this.client.clearPhase(); return; }
      this.formErr = null;
      const id = p.id;
      this.selected = null;
      if (play) void this.client.buyPlay(id, this.minorChecked);
      else void this.client.buy(id, { ...this.buyer }, this.minorChecked);
    });
    btn(row, '취소', PLAIN, 'iap-sheet-cancel', () => { this.selected = null; this.formErr = null; this.minorChecked = false; this.client.version++; this.client.clearPhase(); });
  }
}

/**
 * 설정 창의 「구매 내역」 묶음(상점이 보일 때만). 주문마다 상태·금액·청약철회 기한, 기한 안·미사용이면 [청약철회 요청].
 */
export function mountPurchaseHistory(parent: HTMLElement): { refresh(): void } {
  const c = iapClient(), tester = isTester();
  const appCh = isAppChannel();
  const box = el('div', 'display:none;flex-direction:column;gap:8px;padding:12px 14px;border:1px solid rgba(242,193,78,.45);border-radius:12px;background:rgba(10,16,34,.55);', parent);
  box.dataset.testid = 'iap-history';
  let open = false;
  const render = (): void => {
    const visible = c.mode() === 'play' || (!appCh && shopVisible(c.config, tester));
    box.style.display = visible ? 'flex' : 'none';
    if (!visible) return;
    box.textContent = '';
    const head = el('div', 'display:flex;align-items:center;gap:10px;', box);
    el('div', 'font:800 18px ' + FONT + ';color:#fff;', head, '구매 내역');
    if (c.config && !c.config.live) el('span', 'font:800 12px ' + FONT + ';color:#ffcf5a;', head, '테스트 결제');
    btn(head, open ? '접기' : '보기', PLAIN + 'margin-left:auto;min-height:36px;padding:4px 12px;font-size:13px;', 'iap-history-toggle', () => { open = !open; if (open) void c.refresh(); render(); });
    if (!open) return;
    // 설정에서는 청약철회 요청 결과만 보인다(상점 결제 결과는 상점 탭에)
    if ((c.phase.kind === 'done' || c.phase.kind === 'error') && c.phase.src === 'refund') el('div', 'font:700 13px ' + FONT + ';color:' + (c.phase.kind === 'done' ? '#9ff7b4' : '#ffb0b0') + ';line-height:1.45;', box, c.phase.text);
    if (!c.orders.length) { el('div', 'font:600 13px ' + FONT + ';color:#9fb0d8;', box, '구매 내역이 없어요.'); return; }
    for (const o of c.orders) {
      const row = el('div', 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:8px 10px;border-radius:9px;background:rgba(0,0,0,.25);font:600 13px ' + FONT + ';color:#e8eefc;', box);
      row.dataset.testid = 'iap-order-' + o.id;
      el('b', '', row, o.name);
      el('span', 'color:#ffd877;', row, won(o.amount));
      el('span', 'color:#9fb0d8;', row, new Date(o.paidAt ?? o.createdAt).toLocaleString('ko-KR'));
      el('span', 'color:#cfe0ff;', row, orderStatusText(o) + (o.mode === 'test' ? ' · 테스트' : ''));
      if (o.withdrawable) btn(row, '청약철회 요청', PLAIN + 'margin-left:auto;min-height:34px;padding:4px 10px;font-size:12px;', 'iap-refund-' + o.id, () => void c.requestRefund(o.id));
      else if (o.withdrawUntil && o.status === 'paid' && !o.refundState) el('span', 'margin-left:auto;color:#8e9cc4;font-size:12px;', row, '청약철회 기한 지남');
    }
    const refundLink = el('a', 'font:700 12px ' + FONT + ';color:#ffd877;', box, '청약철회·환불 정책 보기');
    refundLink.setAttribute('href', '/refund'); refundLink.setAttribute('target', '_blank'); refundLink.setAttribute('rel', 'noopener noreferrer');
  };
  c.onChange(render);
  render();
  return { refresh: render };
}
